import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  API_ROUTES,
  autoPerSec,
  bottleWeights,
  BOTTLE_CLICKER,
  BOTTLE_CLICKER_INPUT,
  buyUpgrade,
  canPrestige,
  chipTierFor,
  clickMultiplier,
  creditReturn,
  emptyBottleClickerSave,
  estimatedAutoGroszePerSec,
  applyBottleClickerBootSession,
  huntStateFromSave,
  policeStateFromSave,
  pickLoginSave,
  firmaMultiplier,
  formatKaucja,
  formatKaucjaDelta,
  formatKaucjaPerSec,
  clickBindFromKeyCode,
  clickBindFromMouseButton,
  emptyBottleClickerSettings,
  formatClickBind,
  formatHuntClock,
  formatHuntMult,
  huntMultiplier,
  huntTierById,
  clampHuntStreak,
  demoteHuntStreak,
  parseBottleClickerSettings,
  isUpgradeMaxed,
  isUpgradeUnlocked,
  nextChipTier,
  parseBottleClickerSave,
  payoutForBottle,
  plannedBuyCount,
  prestigeChipReward,
  prestigeMultiplier,
  prestigeRemainingGrosze,
  prestigeRunBonus,
  prestigeSave,
  policeCrushedMultiplier,
  policePayoutMultiplier,
  sackRainMultiplier,
  sackSpawnMultiplier,
  spawnPerSec,
  splashCount,
  splashRadiusPx,
  upgradeCost,
  upgradeCostForCount,
  upgradeMaxLevel,
  visibleUpgrades,
  buildSkupPile,
  creditOfflineGrosze,
  hoursFromMs,
  isPreviewSkupPile,
  roundSkupHours,
  type BottleClickerAdminRestartResponse,
  type BottleClickerBottle,
  type BottleClickerBuyMode,
  type BottleClickerLeaderRow,
  type BottleClickerPrestigeResponse,
  type BottleClickerSave,
  type BottleClickerSaveResponse,
  type BottleClickerSettings,
  type BottleClickerUpgradeDef,
  type BottleClickerUpgradeId,
  type BottleReturnContext,
  type BounceEncounter,
  type HuntEncounter,
  type HuntTierDef,
  type HuntTierId,
  type SkupPile,
} from "@ziolo/shared";
import { Button, Card, useFlash } from "@ziolo/ui";
import { createPortal } from "react-dom";
import { ADMIN_LIST, AdminDeleteButton, useAdminListTools } from "../components/admin/AdminChrome.js";
import { BottleClickerOnPageAdmin } from "../components/admin/BottleClickerAdminTools.js";
import { BottleField, type BottleFieldHandle, type BottleFieldSession } from "../games/bottle-clicker/BottleField.js";
import { SkupOverlay } from "../games/bottle-clicker/SkupOverlay.js";
import { loadSkupPileStore } from "../games/bottle-clicker/skupPileLayouts.js";
import {
  playBottleSfx,
  setBottleSfxMuted,
  setBottleSfxVolume,
  unlockBottleSfx,
} from "../games/bottle-clicker/bottleSound.js";
import { WyplataCouponDrawer } from "../games/bottle-clicker/WyplataCouponDrawer.js";
import { WyplataReceiptOverlay } from "../games/bottle-clicker/WyplataReceiptOverlay.js";
import { couponToReceipt, type WypłataReceiptData } from "../games/bottle-clicker/wyplataReceipt.js";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { confirmAdmin } from "../hooks/useIsAdmin.js";
import { useSession } from "../session/SessionProvider.js";
import "../styles/bottle-clicker.css";

const SAVE_KEY = "ziolo.bottle-clicker.save";
const SETTINGS_KEY = "ziolo.bottle-clicker.settings";
const BUY_MODES: BottleClickerBuyMode[] = [1, 10, "max"];
const COMBO_HUD_MS = 1600;
const CLOUD_DEBOUNCE_MS = 8_000;
const LEADERBOARD_POLL_MS = 30_000;
/** Live zł/s reads the last few seconds, so a combo shows up without flickering. */
const RATE_WINDOW_MS = 5_000;
const RATE_TICK_MS = 500;

function loadBoot(): { save: BottleClickerSave; pile: SkupPile | null } {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    const parsed = raw ? parseBottleClickerSave(JSON.parse(raw) as unknown) : emptyBottleClickerSave();
    const pile = liveSkupPile(parsed);
    const booted = applyBottleClickerBootSession(parsed, Date.now(), pile != null);
    if (pile) return { save: booted.save, pile };
    return { save: persistSave(booted.save), pile: null };
  } catch {
    return { save: emptyBottleClickerSave(), pile: null };
  }
}

function liveSkupPile(save: BottleClickerSave, now = Date.now()): SkupPile | null {
  return buildSkupPile(save, now, loadSkupPileStore().layouts);
}

function persistSave(save: BottleClickerSave, keepLastSeen = false): BottleClickerSave {
  const stamped = keepLastSeen ? save : { ...save, lastSeenMs: Date.now() };
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(stamped));
  } catch {
    // Private mode — the run just does not persist.
  }
  return stamped;
}

function mergeFieldSession(save: BottleClickerSave, session: BottleFieldSession | null): BottleClickerSave {
  if (!session) return save;
  return {
    ...save,
    huntEncounter: session.huntEncounter,
    nextHuntRemainingMs: session.nextHuntRemainingMs,
    policeEncounter: session.policeEncounter,
    nextPoliceRemainingMs: session.nextPoliceRemainingMs,
    nextSackRemainingMs: session.nextSackRemainingMs,
  };
}

function withOwner(save: BottleClickerSave, userId: number | null | undefined): BottleClickerSave {
  if (userId == null) return save;
  if (save.ownerUserId === userId) return save;
  return { ...save, ownerUserId: userId };
}

function loadSettings(): BottleClickerSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? parseBottleClickerSettings(JSON.parse(raw) as unknown) : emptyBottleClickerSettings();
  } catch {
    return emptyBottleClickerSettings();
  }
}

function persistSettings(settings: BottleClickerSettings): BottleClickerSettings {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Private mode — the bind just does not persist.
  }
  return settings;
}

function formatRate(value: number): string {
  return value.toFixed(2).replace(".", ",");
}

function pluralBottles(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (count === 1) return "1 butelka";
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${count} butelki`;
  }
  return `${count} butelek`;
}

function buyModeLabel(mode: BottleClickerBuyMode): string {
  return mode === "max" ? "Max" : `×${mode}`;
}

function formatMult(value: number): string {
  return `×${value.toFixed(2).replace(".", ",")}`;
}

function formatChips(amount: number): string {
  if (amount >= 1_000_000 && amount % 1_000_000 === 0) return `${amount / 1_000_000} mln`;
  return amount.toLocaleString("pl-PL");
}

function formatChipWait(seconds: number): string {
  const total = Math.max(60, Math.ceil(seconds / 60) * 60);
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  if (hours <= 0) return `${minutes} min`;
  if (minutes <= 0) return `${hours} h`;
  return `${hours} h ${minutes} min`;
}

function remainingRestartsCopy(count: number): string {
  if (count === 1) return "Za 1 wypłatę";
  const lastTwo = count % 100;
  const last = count % 10;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return `Za ${count} wypłaty`;
  return `Za ${count} wypłat`;
}

function effectLine(upgrade: BottleClickerUpgradeDef, save: BottleClickerSave): string {
  const level = save.upgrades[upgrade.id];
  if (upgrade.id === "spawn") return `${formatRate(spawnPerSec(level))} but./s`;
  if (upgrade.id === "auto") {
    return level === 0 ? "Gówniarze z osiedla" : `${formatRate(autoPerSec(level))} but./s`;
  }
  if (upgrade.id === "grab") return `Promień ${splashRadiusPx(level, save.upgrades.click)} px`;
  if (upgrade.id === "click") {
    const bonus = Math.round((clickMultiplier(level, true) - 1) * 100);
    return `${splashCount(level)} but. · +${bonus}%`;
  }
  if (upgrade.id === "firma") {
    const bonus = Math.round((firmaMultiplier(level) - 1) * 100);
    return bonus === 0 ? "Mnożnik kaucji" : `Kaucja +${bonus}%`;
  }
  if (upgrade.id === "sack") {
    return `Deszcz ${formatMult(sackRainMultiplier(level))}`;
  }
  const mix = bottleWeights(level);
  if (mix.glass > 0) return "PET · puszki · szkło";
  if (mix.can > 0) return "PET · puszki 50 gr";
  return "PET 50 gr";
}

export function BottleClickerPage() {
  const { push } = useFlash();
  const { user, restoring } = useSession();
  const boot = useRef<ReturnType<typeof loadBoot> | null>(null);
  if (boot.current === null) boot.current = loadBoot();
  const bootSave = boot.current.save;
  const bootNow = Date.now();
  const bootHuntOn = bootSave.huntBuffUntilMs > bootNow;
  const bootHuntTier = bootHuntOn && bootSave.huntBuffTier ? huntTierById(bootSave.huntBuffTier) : null;

  const [save, setSave] = useState(bootSave);
  const [skup, setSkup] = useState<SkupPile | null>(boot.current.pile);
  const [skupPhase, setSkupPhase] = useState<"greet" | "clean">("greet");
  const [skupPopped, setSkupPopped] = useState<Set<number>>(() => new Set());
  const skupRef = useRef(skup);
  const skupDone = useRef(false);
  skupRef.current = skup;
  const [combo, setCombo] = useState(0);
  const [buyMode, setBuyMode] = useState<BottleClickerBuyMode>(1);
  const [board, setBoard] = useState<BottleClickerLeaderRow[]>([]);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [boardReady, setBoardReady] = useState(false);
  const [prestigeBusy, setPrestigeBusy] = useState(false);
  const [chipReadyAtMs, setChipReadyAtMs] = useState(0);
  const [chipWaitLeftMs, setChipWaitLeftMs] = useState(0);
  const [receipt, setReceipt] = useState<{ data: WypłataReceiptData; printing: boolean } | null>(
    null,
  );
  const [couponDrawerOpen, setCouponDrawerOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<"shop" | "wyplata" | "ranking" | null>(null);
  const [sfxMuted, setSfxMuted] = useState(() => !loadSettings().sound);
  const [pileStore] = useState(loadSkupPileStore);
  const [pileNonce, setPileNonce] = useState(0);
  const [liveRate, setLiveRate] = useState(() => estimatedAutoGroszePerSec(save));
  const [huntLeftMs, setHuntLeftMs] = useState(() => Math.max(0, bootSave.huntBuffUntilMs - bootNow));
  const [huntBuff, setHuntBuff] = useState<HuntTierDef | null>(bootHuntTier);
  const [sackLeftMs, setSackLeftMs] = useState(() => Math.max(0, bootSave.sackBuffUntilMs - bootNow));
  const [policeLeftMs, setPoliceLeftMs] = useState(() => Math.max(0, bootSave.policeBuffUntilMs - bootNow));
  const [settings, setSettings] = useState(loadSettings);
  const [capturingBind, setCapturingBind] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const bindWrapRef = useRef<HTMLDivElement>(null);
  const soundWrapRef = useRef<HTMLDivElement>(null);
  const [bindHost, setBindHost] = useState<HTMLElement | null>(null);
  const saveRef = useRef(save);
  const rateSamples = useRef<{ atMs: number; lifetime: number }[]>([]);
  const comboTimer = useRef(0);
  const cloudTimer = useRef(0);
  const cloudReady = useRef(false);
  const huntUntil = useRef(bootSave.huntBuffUntilMs);
  const huntMultRef = useRef<number>(bootHuntTier?.mult ?? BOTTLE_CLICKER.HUNT_MULT);
  const sackUntil = useRef(bootSave.sackBuffUntilMs);
  const policeUntil = useRef(bootSave.policeBuffUntilMs);
  const fieldRef = useRef<BottleFieldHandle>(null);
  const userIdRef = useRef<number | null>(null);
  const [fieldEpoch, setFieldEpoch] = useState(0);
  saveRef.current = skupRef.current != null ? save : { ...save, lastSeenMs: Date.now() };
  userIdRef.current = user?.id ?? null;

  const applySessionHud = useCallback((next: BottleClickerSave, now = Date.now()) => {
    huntUntil.current = next.huntBuffUntilMs;
    const huntOn = next.huntBuffUntilMs > now;
    const tier = huntOn && next.huntBuffTier ? huntTierById(next.huntBuffTier) : null;
    huntMultRef.current = tier?.mult ?? BOTTLE_CLICKER.HUNT_MULT;
    setHuntBuff(tier);
    setHuntLeftMs(Math.max(0, next.huntBuffUntilMs - now));
    policeUntil.current = next.policeBuffUntilMs;
    setPoliceLeftMs(Math.max(0, next.policeBuffUntilMs - now));
    sackUntil.current = next.sackBuffUntilMs;
    setSackLeftMs(Math.max(0, next.sackBuffUntilMs - now));
  }, []);

  const withBuffs = useCallback((ctx: BottleReturnContext): BottleReturnContext => {
    const hunt = Date.now() < huntUntil.current;
    return {
      ...ctx,
      hunt,
      police: Date.now() < policeUntil.current,
      ...(hunt ? { huntMult: huntMultRef.current } : {}),
    };
  }, []);

  const getPayout = useCallback(
    (type: BottleClickerBottle, ctx: BottleReturnContext) =>
      payoutForBottle(saveRef.current, type, withBuffs(ctx)),
    [withBuffs],
  );

  const onHuntCatch = useCallback(
    (tierId: HuntTierId) => {
      const tier = huntTierById(tierId);
      const until = Date.now() + tier.buffSec * 1000;
      huntUntil.current = until;
      huntMultRef.current = tier.mult;
      setHuntLeftMs(tier.buffSec * 1000);
      setHuntBuff(tier);
      setSave((current) => ({
        ...current,
        huntStreak: clampHuntStreak(current.huntStreak + 1),
        huntBuffUntilMs: until,
        huntBuffTier: tier.id,
        huntEncounter: null,
      }));
      push(`Zrekrutowałeś ${tier.caughtName}, ×${formatHuntMult(tier.mult)}.`, "success");
    },
    [push],
  );

  const onHuntMiss = useCallback(() => {
    setSave((current) => {
      const next = demoteHuntStreak(current.huntStreak);
      if (next === current.huntStreak) return current;
      return { ...current, huntStreak: next, huntEncounter: null };
    });
  }, []);

  const onHuntSession = useCallback(
    (encounter: HuntEncounter | null, nextRemainingMs: number) => {
      setSave((current) => {
        if (
          current.huntEncounter === encounter &&
          current.nextHuntRemainingMs === nextRemainingMs
        ) {
          return current;
        }
        return {
          ...current,
          huntEncounter: encounter,
          nextHuntRemainingMs: nextRemainingMs,
        };
      });
    },
    [],
  );

  const onPoliceCatch = useCallback(() => {
    const until = Date.now() + BOTTLE_CLICKER.POLICE_BUFF_SEC * 1000;
    policeUntil.current = until;
    setPoliceLeftMs(BOTTLE_CLICKER.POLICE_BUFF_SEC * 1000);
    setSave((current) => ({
      ...current,
      policeBuffUntilMs: until,
      policeEncounter: null,
    }));
    push("Policja zatrzymała twoich meneli.", "error");
  }, [push]);

  const onPoliceSession = useCallback((encounter: BounceEncounter | null, nextRemainingMs: number) => {
    setSave((current) => {
      if (current.policeEncounter === encounter && current.nextPoliceRemainingMs === nextRemainingMs) {
        return current;
      }
      return {
        ...current,
        policeEncounter: encounter,
        nextPoliceRemainingMs: nextRemainingMs,
      };
    });
  }, []);

  const onSackCatch = useCallback(() => {
    const rain = sackRainMultiplier(saveRef.current.upgrades.sack);
    const until = Date.now() + BOTTLE_CLICKER.SACK_BUFF_SEC * 1000;
    sackUntil.current = until;
    setSackLeftMs(BOTTLE_CLICKER.SACK_BUFF_SEC * 1000);
    setSave((current) => ({ ...current, sackBuffUntilMs: until }));
    push(
      `Worek! Deszcz butelek ${formatMult(rain)} na ${BOTTLE_CLICKER.SACK_BUFF_SEC} s.`,
      "success",
    );
  }, [push]);

  const onSackCooldown = useCallback((nextRemainingMs: number) => {
    setSave((current) =>
      current.nextSackRemainingMs === nextRemainingMs
        ? current
        : { ...current, nextSackRemainingMs: nextRemainingMs },
    );
  }, []);

  const patchSettings = useCallback((next: BottleClickerSettings) => {
    setSettings(persistSettings(next));
    setBottleSfxMuted(!next.sound);
    setBottleSfxVolume(next.volume);
    setSfxMuted(!next.sound);
  }, []);

  useEffect(() => {
    setBottleSfxMuted(!settings.sound);
    setBottleSfxVolume(settings.volume);
    setSfxMuted(!settings.sound);
  }, [settings.sound, settings.volume]);

  useEffect(() => {
    if (!capturingBind) return;
    function finish(next: BottleClickerSettings["clickBind"]) {
      setCapturingBind(false);
      if (next) patchSettings({ ...settings, clickBind: next });
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.repeat) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.code === "Escape") {
        setCapturingBind(false);
        return;
      }
      const bind = clickBindFromKeyCode(event.code);
      if (bind) finish(bind);
    }
    function onPointerDown(event: PointerEvent) {
      if (event.button === 0) {
        const target = event.target;
        if (target instanceof Element && target.closest("[data-bind-capture]")) return;
        setCapturingBind(false);
        return;
      }
      const bind = clickBindFromMouseButton(event.button);
      if (!bind) return;
      event.preventDefault();
      event.stopPropagation();
      finish(bind);
    }
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [capturingBind, patchSettings, settings]);

  useEffect(() => {
    if (!mobilePanel) return;
    function onKey(event: KeyboardEvent) {
      if (event.code === "Escape") closeMobilePanel();
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobilePanel]);

  useEffect(() => {
    if (!bindOpen && !soundOpen) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && bindWrapRef.current?.contains(target)) return;
      if (target instanceof Node && soundWrapRef.current?.contains(target)) return;
      setBindOpen(false);
      setSoundOpen(false);
      setCapturingBind(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.code !== "Escape" || capturingBind) return;
      setBindOpen(false);
      setSoundOpen(false);
    }
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [bindOpen, soundOpen, capturingBind]);

  useLayoutEffect(() => {
    setBindHost(document.getElementById("z-userbar-extra"));
  }, [user, restoring]);

  /**
   * Lifetime kaucja is monotonic, so the live rate is just how much of it arrived
   * over the sample window. Offline credit lands in one lump, so whoever applies
   * it drops the samples instead of letting them read as an eight-hour combo.
   */
  const forgetRateHistory = useCallback(() => {
    rateSamples.current = [];
    setLiveRate(0);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const samples = rateSamples.current;
      const now = performance.now();
      samples.push({ atMs: now, lifetime: saveRef.current.lifetimeGrosze });
      while (samples.length > 2 && now - (samples[0]?.atMs ?? now) > RATE_WINDOW_MS) {
        samples.shift();
      }
      const first = samples[0];
      const last = samples.at(-1);
      if (!first || !last || last.atMs <= first.atMs) return;
      const seconds = (last.atMs - first.atMs) / 1000;
      setLiveRate(Math.max(0, (last.lifetime - first.lifetime) / seconds));
    }, RATE_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const huntLeft = Math.max(0, huntUntil.current - Date.now());
      const rainLeft = Math.max(0, sackUntil.current - Date.now());
      const policeLeft = Math.max(0, policeUntil.current - Date.now());
      const chipLeft = Math.max(0, chipReadyAtMs - Date.now());
      setHuntLeftMs((current) => (current === huntLeft ? current : huntLeft));
      setSackLeftMs((current) => (current === rainLeft ? current : rainLeft));
      setPoliceLeftMs((current) => (current === policeLeft ? current : policeLeft));
      setChipWaitLeftMs((current) => (current === chipLeft ? current : chipLeft));
    }, 250);
    return () => window.clearInterval(timer);
  }, [chipReadyAtMs]);

  const applyChipWait = useCallback((sec: number | undefined) => {
    const wait = Math.max(0, Math.floor(sec ?? 0));
    setChipReadyAtMs(wait > 0 ? Date.now() + wait * 1000 : 0);
    setChipWaitLeftMs(wait * 1000);
  }, []);

  const putCloud = useCallback(
    async (next: BottleClickerSave) => {
      if (!cloudReady.current) return;
      try {
        const body = await apiRequest<BottleClickerSaveResponse>(API_ROUTES.bottleClicker.save, {
          method: "PUT",
          body: { save: next },
        });
        applyChipWait(body.chipWaitSec);
        // The server kept its own run: the upload claimed kaucja nobody could earn.
        if (body.rejected && body.save) {
          const kept = persistSave(parseBottleClickerSave(body.save));
          saveRef.current = kept;
          setSave(kept);
          applySessionHud(kept);
          setFieldEpoch((n) => n + 1);
          forgetRateHistory();
          push("Zapis odrzucony — kaucja rosła szybciej, niż da się zagrać. Wrócił zapis z konta.", "error");
        } else if (body.stale && body.save) {
          const kept = persistSave(parseBottleClickerSave(body.save));
          saveRef.current = kept;
          setSave(kept);
          applySessionHud(kept);
          setFieldEpoch((n) => n + 1);
          forgetRateHistory();
          push(
            kept.lifetimeGrosze === 0 && kept.prestigeCount === 0
              ? "Admin wyzerował biznes. Start od zera."
              : "Admin zmienił zapis. Wczytano wersję z konta.",
            "success",
          );
        }
      } catch {
        // Offline or logged out — localStorage still has the run.
      }
    },
    [push, forgetRateHistory, applyChipWait, applySessionHud],
  );

  /** One nudge per flag window; the field fires on every machine-made tap. */
  const macroNoticeAt = useRef(0);
  const onMacroTap = useCallback(() => {
    const now = Date.now();
    if (now - macroNoticeAt.current < BOTTLE_CLICKER_INPUT.FLAG_MS) return;
    macroNoticeAt.current = now;
    push("Klikanie w rytm automatu — bonus kliknięcia i seria wyłączone. Kliknij nierówno.", "error");
  }, [push]);

  const reloadBoard = useCallback(async () => {
    try {
      const rows = await apiRequest<BottleClickerLeaderRow[]>(API_ROUTES.bottleClicker.leaderboard);
      setBoard(rows);
      setBoardError(null);
    } catch (cause) {
      setBoardError(errorMessage(cause));
    } finally {
      setBoardReady(true);
    }
  }, []);

  const admin = useAdminListTools({
    ...ADMIN_LIST.bottleClicker,
    onReload: reloadBoard,
  });

  const applyCloudSave = useCallback(async () => {
    const remote = await apiRequest<BottleClickerSaveResponse>(API_ROUTES.bottleClicker.save);
    applyChipWait(remote.chipWaitSec);
    if (remote.save) {
      const kept = persistSave(parseBottleClickerSave(remote.save));
      saveRef.current = kept;
      setSave(kept);
      applySessionHud(kept);
      setFieldEpoch((n) => n + 1);
      forgetRateHistory();
    }
    await reloadBoard();
  }, [forgetRateHistory, reloadBoard, applyChipWait, applySessionHud]);

  function addAwayHours(hours: number) {
    const hourMs = 60 * 60 * 1000;
    const now = Date.now();
    const cap = BOTTLE_CLICKER.OFFLINE_CAP_MS;
    const currentSeen = save.lastSeenMs > 0 && save.lastSeenMs <= now ? save.lastSeenMs : now;
    const nextSeen = Math.max(now - cap, currentSeen - hours * hourMs);
    if (now - currentSeen >= cap - 1000) {
      push("Skup liczy najwyżej 8 godzin nieobecności.", "error");
      return;
    }
    const next = { ...save, lastSeenMs: nextSeen };
    const pile = liveSkupPile(next, now);
    if (!pile) {
      push("Kup zbieraczy — bez nich nieobecność nie zostawia worków.", "error");
      return;
    }
    skupRef.current = pile;
    const kept = persistSave(next, true);
    saveRef.current = kept;
    setSave(kept);
    setSkup(pile);
    const awayH = Math.max(1, Math.round((now - nextSeen) / hourMs));
    setPileNonce((n) => n + 1);
    push(`Nieobecność ${awayH} h. Worki czekają na parkingu.`, "success");
    void putCloud(kept);
  }

  async function restartAllPlayers() {
    if (
      !confirmAdmin(
        "Wyzerować Bottle clicker wszystkich zalogowanych graczy? Kaucja, sklepy, prestiż i ranking wracają do zera. Bez żetonów kasyna.",
      )
    ) {
      return;
    }
    try {
      const result = await apiRequest<BottleClickerAdminRestartResponse>(
        API_ROUTES.admin.bottleClickerRestart,
        { method: "POST" },
      );
      const remote = await apiRequest<BottleClickerSaveResponse>(API_ROUTES.bottleClicker.save);
      if (remote.save) {
        const kept = persistSave(parseBottleClickerSave(remote.save));
        saveRef.current = kept;
        setSave(kept);
        applySessionHud(kept);
        setFieldEpoch((n) => n + 1);
        forgetRateHistory();
      }
      await reloadBoard();
      push(
        result.restarted === 1
          ? "Wyzerowano 1 biznes."
          : `Wyzerowano ${result.restarted} biznesów.`,
        "success",
      );
    } catch (cause) {
      push(errorMessage(cause), "error");
    }
  }

  useEffect(() => {
    const merged = mergeFieldSession(save, fieldRef.current?.snapshotSession() ?? null);
    saveRef.current = persistSave(withOwner(merged, userIdRef.current), skupRef.current != null);
    window.clearTimeout(cloudTimer.current);
    if (!cloudReady.current) return;
    cloudTimer.current = window.setTimeout(() => {
      void putCloud(saveRef.current);
    }, CLOUD_DEBOUNCE_MS);
  }, [save, skup, putCloud]);

  useEffect(() => {
    void reloadBoard();
    const timer = window.setInterval(() => void reloadBoard(), LEADERBOARD_POLL_MS);
    return () => window.clearInterval(timer);
  }, [reloadBoard]);

  useEffect(() => {
    if (restoring) return;
    if (!user) {
      cloudReady.current = false;
      return;
    }

    let cancelled = false;
    cloudReady.current = false;

    void (async () => {
      try {
        const remote = await apiRequest<BottleClickerSaveResponse>(API_ROUTES.bottleClicker.save);
        if (cancelled) return;
        applyChipWait(remote.chipWaitSec);
        const local = saveRef.current;
        const picked = pickLoginSave(
          local,
          remote.save ? parseBottleClickerSave(remote.save) : null,
          user.id,
        );
        const pile = liveSkupPile(picked);
        const booted = applyBottleClickerBootSession(picked, Date.now(), pile != null);
        const next = pile ? booted.save : persistSave(withOwner(booted.save, user.id), false);
        skupRef.current = pile;
        setSkup(pile);
        saveRef.current = next;
        setSave(next);
        applySessionHud(next);
        setFieldEpoch((n) => n + 1);
        forgetRateHistory();
        cloudReady.current = true;
        await putCloud(next);
        void reloadBoard();
      } catch {
        if (!cancelled) cloudReady.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, restoring, push, putCloud, reloadBoard, forgetRateHistory, applyChipWait, applySessionHud]);

  useEffect(() => {
    function onVisibility() {
      if (document.hidden) {
        const merged = mergeFieldSession(saveRef.current, fieldRef.current?.snapshotSession() ?? null);
        const stamped = persistSave(withOwner(merged, userIdRef.current), skupRef.current != null);
        saveRef.current = stamped;
        window.clearTimeout(cloudTimer.current);
        void putCloud(stamped);
        return;
      }
      if (skupRef.current) return;
      const pile = liveSkupPile(saveRef.current);
      if (pile) {
        skupRef.current = pile;
        setSkup(pile);
        return;
      }
      const next = persistSave(withOwner(saveRef.current, userIdRef.current));
      saveRef.current = next;
      setSave(next);
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [push, putCloud, forgetRateHistory]);

  useEffect(() => {
    return () => {
      window.clearTimeout(comboTimer.current);
      window.clearTimeout(cloudTimer.current);
      void putCloud(
        persistSave(
          withOwner(
            mergeFieldSession(saveRef.current, fieldRef.current?.snapshotSession() ?? null),
            userIdRef.current,
          ),
          skupRef.current != null,
        ),
      );
    };
  }, [putCloud]);

  const resolveSkup = useCallback(
    (grosze: number) => {
      const pile = skupRef.current;
      skupRef.current = null;
      setSkup(null);
      if (isPreviewSkupPile(pile)) return;
      setSave((current) => creditOfflineGrosze(current, grosze));
      forgetRateHistory();
      if (!pile) return;
      push(
        grosze >= pile.manualGrosze
          ? `Parking czysty: ${formatKaucjaDelta(grosze)}`
          : `Menel wzięli haracz. Zostaje ${formatKaucjaDelta(grosze)}`,
        "success",
      );
    },
    [forgetRateHistory, push],
  );

  useEffect(() => {
    skupDone.current = false;
    setSkupPopped(new Set());
    setSkupPhase("greet");
  }, [skup?.ms, skup?.hireGrosze]);

  useEffect(() => {
    if (!skup || skupPhase !== "clean" || skupDone.current) return;
    if (skupPopped.size < skup.bags.length) return;
    skupDone.current = true;
    resolveSkup(skup.manualGrosze);
  }, [skup, skupPhase, skupPopped, resolveSkup]);

  function buy(id: BottleClickerUpgradeId) {
    setSave((current) => {
      if (isUpgradeMaxed(current, id)) return current;
      const count = plannedBuyCount(current, id, buyMode);
      const next = buyUpgrade(current, id, count);
      if (next) playBottleSfx("buy");
      return next ?? current;
    });
  }

  function openMobilePanel(panel: "shop" | "wyplata" | "ranking") {
    unlockBottleSfx();
    setMobilePanel((cur) => {
      if (cur === panel) {
        playBottleSfx("panelClose");
        return null;
      }
      playBottleSfx(cur ? "ui" : "panelOpen");
      return panel;
    });
  }

  function closeMobilePanel() {
    if (mobilePanel) playBottleSfx("panelClose");
    setMobilePanel(null);
  }

  async function restartRun() {
    const current = saveRef.current;
    if (!canPrestige(current) || prestigeBusy) return;
    const nextBonus = current.prestigeBonus + prestigeRunBonus(current.runLifetimeGrosze);
    const nextMultValue = prestigeMultiplier(nextBonus);
    const nextMult = formatMult(nextMultValue);
    const chips = formatChips(prestigeChipReward(current.prestigeCount));
    const chipsReadyNow = !user || chipWaitLeftMs <= 0;
    const confirmText = !user
      ? `Wypłacić kaucję? Sklepy i portfel wracają do zera. Ranking i ${nextMult} kaucji zostają. Żetony kasyna dostaniesz tylko po zalogowaniu.`
      : chipsReadyNow
        ? `Wypłacić kaucję? Sklepy i portfel wracają do zera. Ranking i ${nextMult} kaucji zostają. Na konto kasyna wpada ${chips} żetonów.`
        : `Wypłacić kaucję? Sklepy i portfel wracają do zera. Ranking i ${nextMult} kaucji zostają. Bez żetonów — kolejne za ${formatChipWait(Math.ceil(chipWaitLeftMs / 1000))}.`;
    if (!window.confirm(confirmText)) return;

    if (!user) {
      const next = prestigeSave(current, Date.now(), { chipsGranted: 0 });
      if (!next) return;
      const kept = persistSave(withOwner(next, userIdRef.current));
      setSave(kept);
      applySessionHud(kept);
      setFieldEpoch((n) => n + 1);
      const slip = next.coupons[0];
      if (slip) {
        playBottleSfx("print");
        setReceipt({ data: couponToReceipt(slip), printing: true });
      }
      return;
    }

    setPrestigeBusy(true);
    window.clearTimeout(cloudTimer.current);
    try {
      await apiRequest<BottleClickerSaveResponse>(API_ROUTES.bottleClicker.save, {
        method: "PUT",
        body: { save: persistSave(current) },
      });
      const result = await apiRequest<BottleClickerPrestigeResponse>(API_ROUTES.bottleClicker.prestige, {
        method: "POST",
      });
      setSave(persistSave(withOwner(result.save, userIdRef.current)));
      applySessionHud(result.save);
      setFieldEpoch((n) => n + 1);
      applyChipWait(result.chipWaitSec);
      const slip = result.save.coupons[0];
      if (slip) {
        playBottleSfx("print");
        setReceipt({ data: couponToReceipt(slip), printing: true });
      }
      void reloadBoard();
    } catch (cause) {
      push(errorMessage(cause), "error");
    } finally {
      setPrestigeBusy(false);
    }
  }

  const rainMult = sackSpawnMultiplier(save.upgrades.sack, sackUntil.current);
  const crushedMult = policeCrushedMultiplier(policeUntil.current);
  const idle =
    estimatedAutoGroszePerSec(save, rainMult) *
    huntMultiplier(huntUntil.current, Date.now(), huntMultRef.current) *
    policePayoutMultiplier(policeUntil.current);
  const huntActive = huntLeftMs > 0;
  const sackActive = sackLeftMs > 0;
  const policeActive = policeLeftMs > 0;
  const chipReward = prestigeChipReward(save.prestigeCount);
  const upcomingTier = nextChipTier(save.prestigeCount);
  const catalog = visibleUpgrades(save);
  const readyToPrestige = canPrestige(save);
  const prestigeLeft = prestigeRemainingGrosze(save);
  const prestigeProgress = Math.min(1, save.runLifetimeGrosze / BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE);
  const runMult = prestigeMultiplier(save.prestigeBonus);
  const previewRunGrosze = readyToPrestige
    ? save.runLifetimeGrosze
    : Math.max(save.runLifetimeGrosze, BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE);
  const runBonus = prestigeRunBonus(previewRunGrosze);
  const nextRunMult = prestigeMultiplier(save.prestigeBonus + runBonus);
  const runBonusPct = Math.round(runBonus * 100);
  const chipsReady = !user || chipWaitLeftMs <= 0;
  const chipWaitLabel =
    chipWaitLeftMs > 0 ? formatChipWait(Math.ceil(chipWaitLeftMs / 1000)) : null;

  function renderBuyModeControls() {
    return (
      <div className="bottle-clicker-shop__modes" role="group" aria-label="Ile kupić">
        {BUY_MODES.map((mode) => (
          <Button
            key={String(mode)}
            size="sm"
            variant={buyMode === mode ? "primary" : "ghost"}
            aria-pressed={buyMode === mode}
            onClick={() => setBuyMode(mode)}
          >
            {buyModeLabel(mode)}
          </Button>
        ))}
      </div>
    );
  }

  function renderCouponButton() {
    return (
      <Button
        size="sm"
        variant={couponDrawerOpen ? "primary" : "ghost"}
        className={`bottle-clicker-bind__icon${save.coupons.length > 0 ? " is-bound" : ""}`}
        aria-label={
          save.coupons.length > 0
            ? `Historia kuponów (${save.coupons.length})`
            : "Historia kuponów"
        }
        aria-expanded={couponDrawerOpen}
        title="Historia kuponów"
        onClick={() => {
          setBindOpen(false);
          setSoundOpen(false);
          setCapturingBind(false);
          setMobilePanel(null);
          setCouponDrawerOpen((open) => !open);
        }}
      >
        🧾
      </Button>
    );
  }

  const toolbar = (
    <div className="bottle-clicker-tools">
      <div className="bottle-clicker-coupon-wrap">{renderCouponButton()}</div>
      <div className="bottle-clicker-bind-wrap" ref={soundWrapRef}>
        <Button
          type="button"
          size="sm"
          variant={soundOpen || !sfxMuted ? "primary" : "ghost"}
          className="bottle-clicker-bind__icon"
          aria-label="Głośność dźwięków"
          aria-expanded={soundOpen}
          aria-controls="bottle-clicker-sound-panel"
          title="Głośność"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            unlockBottleSfx();
            setCouponDrawerOpen(false);
            setBindOpen(false);
            setCapturingBind(false);
            setSoundOpen((open) => !open);
          }}
        >
          {sfxMuted || settings.volume <= 0 ? "🔇" : settings.volume < 0.35 ? "🔉" : "🔊"}
        </Button>
        {soundOpen ? (
          <div
            id="bottle-clicker-sound-panel"
            className="bottle-clicker-bind__panel bottle-clicker-sound__panel"
            role="dialog"
            aria-label="Dźwięki"
          >
            <div className="bottle-clicker-bind">
              <span>Dźwięki</span>
              <Button
                type="button"
                size="sm"
                variant={sfxMuted ? "ghost" : "primary"}
                aria-pressed={!sfxMuted}
                onClick={() => {
                  unlockBottleSfx();
                  const nextSound = sfxMuted;
                  patchSettings({ ...settings, sound: nextSound });
                  if (nextSound) playBottleSfx("ui");
                }}
              >
                {sfxMuted ? "Wyciszone" : "Włączone"}
              </Button>
            </div>
            <label className="bottle-clicker-sound__volume">
              <span>Głośność · {Math.round(settings.volume * 100)}%</span>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(settings.volume * 100)}
                aria-valuetext={`${Math.round(settings.volume * 100)} procent`}
                onChange={(event) => {
                  unlockBottleSfx();
                  const volume = Number(event.target.value) / 100;
                  const next = {
                    ...settings,
                    volume,
                    sound: volume <= 0 ? false : true,
                  };
                  patchSettings(next);
                  if (next.sound) playBottleSfx("ui");
                }}
              />
            </label>
          </div>
        ) : null}
      </div>
      <div className="bottle-clicker-bind-wrap" ref={bindWrapRef} data-bind-capture="">
        <Button
          size="sm"
          variant={bindOpen || capturingBind ? "primary" : "ghost"}
          className={`bottle-clicker-bind__icon${settings.clickBind ? " is-bound" : ""}`}
          aria-label={
            settings.clickBind ? `Sterowanie: ${formatClickBind(settings.clickBind)}` : "Sterowanie"
          }
          aria-expanded={bindOpen}
          aria-controls="bottle-clicker-bind-panel"
          title="Sterowanie"
          onClick={() => {
            setCouponDrawerOpen(false);
            setSoundOpen(false);
            setBindOpen((open) => !open);
            setCapturingBind(false);
          }}
        >
          🖱️
        </Button>
        {bindOpen ? (
          <div
            id="bottle-clicker-bind-panel"
            className="bottle-clicker-bind__panel"
            role="dialog"
            aria-label="Sterowanie"
          >
            <p className="hub-hint bottle-clicker-bind__copy">
              Klawisz albo boczny przycisk myszy klika tam, gdzie stoi kursor. Przytrzymanie nic nie
              daje.
            </p>
            <div className="bottle-clicker-bind">
              <span>Klawisz klikania</span>
              <div className="bottle-clicker-bind__actions">
                <Button
                  size="sm"
                  variant={capturingBind ? "primary" : "default"}
                  aria-pressed={capturingBind}
                  onClick={() => setCapturingBind((current) => !current)}
                >
                  {capturingBind ? "Naciśnij klawisz…" : formatClickBind(settings.clickBind)}
                </Button>
                {settings.clickBind && !capturingBind ? (
                  <Button size="sm" variant="ghost" onClick={() => patchSettings({ ...settings, clickBind: null })}>
                    Wyczyść
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={`bottle-clicker${mobilePanel ? " bottle-clicker--sheet" : ""}`}>
      {bindHost ? createPortal(toolbar, bindHost) : null}
      <section className="bottle-clicker__stage">
        <header className="bottle-clicker__header">
          {bindHost ? (
            <h2 className="bottle-clicker__title">
              <i className="z-icon" aria-hidden="true">
                🍾
              </i>
              Bottle clicker
            </h2>
          ) : (
            <div className="bottle-clicker__title-row">
              <h2 className="bottle-clicker__title">
                <i className="z-icon" aria-hidden="true">
                  🍾
                </i>
                Bottle clicker
              </h2>
              {toolbar}
            </div>
          )}
          <div className="bottle-clicker__fortune" aria-live="polite">
            <div className="bottle-clicker__kaucja">{formatKaucja(save.grosze)}</div>
            <div className="bottle-clicker__rates">
              <span
                className={
                  liveRate > idle * 1.08 ? "bottle-clicker__rate bottle-clicker__rate--live" : "bottle-clicker__rate"
                }
                title="Kaucja z ostatnich sekund — klikanie, seria i zbieracze razem"
              >
                Aktualnie <strong>{formatKaucjaPerSec(liveRate)}</strong>
              </span>
              <span className="bottle-clicker__rate" title="Tyle wpada samo, bez klikania">
                Idle <strong>{formatKaucjaPerSec(idle)}</strong>
              </span>
              {combo >= 2 ? (
                <span className="bottle-clicker__combo">
                  Seria <strong>×{combo}</strong>
                </span>
              ) : null}
              {huntActive && huntBuff ? (
                <span className={`bottle-clicker__hunt bottle-clicker__hunt--${huntBuff.key}`} title="Bonus do kaucji">
                  {huntBuff.label} <strong>×{formatHuntMult(huntBuff.mult)}</strong> {formatHuntClock(huntLeftMs)}
                </span>
              ) : null}
              {sackActive ? (
                <span className="bottle-clicker__sack" title="Deszcz butelek za złapanie worka">
                  Deszcz <strong>{formatMult(rainMult)}</strong> {formatHuntClock(sackLeftMs)}
                </span>
              ) : null}
              {policeActive ? (
                <span className="bottle-clicker__police" title="Policja zatrzymała twoich meneli">
                  Policja{" "}
                  <strong>
                    ×{BOTTLE_CLICKER.POLICE_PAYOUT_MULT.toFixed(2).replace(".", ",")}
                  </strong>{" "}
                  {formatHuntClock(policeLeftMs)}
                </span>
              ) : null}
            </div>
            {admin.isAdmin ? (
              <>
                <div className="bottle-clicker__admin-away">
                  <span>Nieobecność</span>
                  <Button size="sm" onClick={() => addAwayHours(1)}>
                    +1 h
                  </Button>
                  <Button size="sm" onClick={() => addAwayHours(4)}>
                    +4 h
                  </Button>
                  <Button size="sm" onClick={() => addAwayHours(8)}>
                    +8 h
                  </Button>
                </div>
              </>
            ) : null}
          </div>
          <div className="bottle-clicker__stats">
            <span>
              Oddane: <strong>{pluralBottles(save.returnedBottles)}</strong>
            </span>
            <span>
              Łącznie: <strong>{formatKaucja(save.lifetimeGrosze)}</strong>
            </span>
            <span>
              Ten bieg: <strong>{formatKaucja(save.runLifetimeGrosze)}</strong>
            </span>
            {save.prestigeCount > 0 ? (
              <span>
                Wypłaty: <strong>{save.prestigeCount}</strong> · kaucja <strong>{formatMult(runMult)}</strong>
              </span>
            ) : null}
            {save.bestCombo > 1 ? (
              <span>
                Rekord serii: <strong>×{save.bestCombo}</strong>
              </span>
            ) : null}
          </div>
        </header>

        <BottleField
          key={fieldEpoch}
          ref={fieldRef}
          spawnPerSec={spawnPerSec(save.upgrades.spawn) * rainMult}
          autoPerSec={autoPerSec(save.upgrades.auto)}
          mixLevel={save.upgrades.mix}
          splashRadius={splashRadiusPx(save.upgrades.grab, save.upgrades.click)}
          splashCount={splashCount(save.upgrades.click)}
          returnedBottles={save.returnedBottles}
          kaucjaGrosze={save.grosze}
          getPayout={getPayout}
          onMacroTap={onMacroTap}
          onHuntCatch={onHuntCatch}
          onHuntMiss={onHuntMiss}
          huntStreak={save.huntStreak}
          resumeHunt={save.huntEncounter ? huntStateFromSave(save.huntEncounter, performance.now()) : null}
          nextHuntRemainingMs={save.nextHuntRemainingMs}
          onHuntSession={onHuntSession}
          resumePolice={save.policeEncounter ? policeStateFromSave(save.policeEncounter, performance.now()) : null}
          nextPoliceRemainingMs={save.nextPoliceRemainingMs}
          onPoliceSession={onPoliceSession}
          nextSackRemainingMs={save.nextSackRemainingMs}
          onSackCooldown={onSackCooldown}
          onPoliceCatch={onPoliceCatch}
          onSackCatch={onSackCatch}
          sackLevel={save.upgrades.sack}
          crushedSpawnMult={crushedMult}
          clickBind={capturingBind ? null : settings.clickBind}
          skupBags={skup ? skup.bags.filter((bag) => !skupPopped.has(bag.id)) : null}
          skupBagTotal={skup?.bags.length ?? 0}
          skupHours={roundSkupHours(hoursFromMs(skup?.ms ?? 0))}
          pileLayouts={pileStore.layouts}
          pileTune={pileStore.tune}
          skupLayoutNonce={pileNonce}
          skupInteractive={skupPhase === "clean"}
          onPopSkup={(id) => {
            setSkupPopped((current) => {
              if (current.has(id)) return current;
              const next = new Set(current);
              next.add(id);
              return next;
            });
          }}
          onReturned={(type, ctx) => {
            const paid = withBuffs(ctx);
            if (paid.manual) {
              setCombo(paid.combo);
              window.clearTimeout(comboTimer.current);
              comboTimer.current = window.setTimeout(() => setCombo(0), COMBO_HUD_MS);
            }
            setSave((current) => creditReturn(current, type, paid));
          }}
        >
          {skup && skupPhase === "greet" ? (
            <SkupOverlay
              pile={skup}
              onClean={() => setSkupPhase("clean")}
              onHire={() => resolveSkup(skup.hireGrosze)}
            />
          ) : null}
        </BottleField>
      </section>

      <nav className="bottle-clicker-dock" aria-label="Menu Bottle clicker">
        <button
          type="button"
          className={`bottle-clicker-dock__btn${mobilePanel === "shop" ? " is-active" : ""}`}
          aria-pressed={mobilePanel === "shop"}
          onClick={() => openMobilePanel("shop")}
        >
          <span aria-hidden="true">♻️</span>
          <span>Sklep</span>
        </button>
        <button
          type="button"
          className={`bottle-clicker-dock__btn${mobilePanel === "wyplata" ? " is-active" : ""}`}
          aria-pressed={mobilePanel === "wyplata"}
          onClick={() => openMobilePanel("wyplata")}
        >
          <span aria-hidden="true">🧾</span>
          <span>Wypłata</span>
        </button>
        <button
          type="button"
          className={`bottle-clicker-dock__btn${mobilePanel === "ranking" ? " is-active" : ""}`}
          aria-pressed={mobilePanel === "ranking"}
          onClick={() => openMobilePanel("ranking")}
        >
          <span aria-hidden="true">🏆</span>
          <span>Ranking</span>
        </button>
      </nav>

      <aside className={`bottle-clicker__side${mobilePanel ? " is-open" : ""}`}>
        <button
          type="button"
          className="bottle-clicker__sheet-backdrop"
          aria-label="Zamknij panel"
          tabIndex={mobilePanel ? 0 : -1}
          onClick={closeMobilePanel}
        />
        <div
          className={`bottle-clicker__panel${mobilePanel === "shop" ? " is-open" : ""}`}
          role="dialog"
          aria-label="Biznes kaucyjny"
        >
          <div className="bottle-clicker__panel-head">
            <h3>Biznes kaucyjny</h3>
            {renderBuyModeControls()}
            <button type="button" className="bottle-clicker__panel-close" onClick={closeMobilePanel}>
              Zamknij
            </button>
          </div>
        <Card
          variant="panel"
          icon="♻️"
          title={
            <span className="bottle-clicker-shop__head">
              <span>Biznes kaucyjny</span>
              {renderBuyModeControls()}
            </span>
          }
        >
          <ul className="bottle-clicker-shop">
            {catalog.map((upgrade) => {
              const level = save.upgrades[upgrade.id];
              const unlocked = isUpgradeUnlocked(save, upgrade.id);
              const maxed = isUpgradeMaxed(save, upgrade.id);
              const count = maxed ? 0 : plannedBuyCount(save, upgrade.id, buyMode);
              const affordable = count > 0;
              const cost =
                count > 0
                  ? upgradeCostForCount(upgrade.id, level, count)
                  : upgradeCost(upgrade.id, level);
              const left = Math.max(0, upgrade.unlockLifetime - save.runLifetimeGrosze);
              const restartsLeft = Math.max(0, (upgrade.unlockPrestige ?? 0) - save.prestigeCount);
              const prestigeLocked = restartsLeft > 0 && left <= 0;
              const progress = prestigeLocked
                ? Math.min(1, save.prestigeCount / Math.max(1, upgrade.unlockPrestige ?? 1))
                : upgrade.unlockLifetime <= 0
                  ? 1
                  : Math.min(1, save.runLifetimeGrosze / upgrade.unlockLifetime);
              const lockCopy = prestigeLocked
                ? remainingRestartsCopy(restartsLeft)
                : `Jeszcze ${formatKaucja(left)}`;
              return (
                <li
                  key={upgrade.id}
                  className={[
                    "bottle-clicker-shop__item",
                    unlocked ? "" : "bottle-clicker-shop__item--locked",
                    maxed ? "bottle-clicker-shop__item--maxed" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="bottle-clicker-shop__icon" aria-hidden="true">
                    {upgrade.icon}
                  </span>
                  <div className="bottle-clicker-shop__copy">
                    <span className="bottle-clicker-shop__label">{upgrade.label}</span>
                    <p className="bottle-clicker-shop__flavor">{upgrade.flavor}</p>
                    {unlocked ? (
                      <p className="bottle-clicker-shop__effect">{effectLine(upgrade, save)}</p>
                    ) : (
                      <>
                        <p className="bottle-clicker-shop__effect bottle-clicker-shop__effect--lock">
                          {lockCopy}
                        </p>
                        <div
                          className="bottle-clicker-shop__bar"
                          role="progressbar"
                          aria-label={`${upgrade.label}: odblokowanie`}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={Math.round(progress * 100)}
                        >
                          <span style={{ width: `${Math.round(progress * 100)}%` }} />
                        </div>
                      </>
                    )}
                  </div>
                  <span
                    className="bottle-clicker-shop__owned"
                    aria-label={unlocked ? `Posiadane: ${level} z ${upgradeMaxLevel(upgrade.id)}` : "Zablokowane"}
                  >
                    {unlocked ? level : "–"}
                  </span>
                  {unlocked && maxed ? (
                    <span
                      className="bottle-clicker-shop__buy bottle-clicker-shop__buy--maxed"
                      aria-label={`${upgrade.label}: maksimum ${level}`}
                    >
                      Max
                    </span>
                  ) : unlocked ? (
                    <Button
                      variant="primary"
                      size="sm"
                      className="bottle-clicker-shop__buy"
                      disabled={!affordable}
                      aria-label={
                        count > 1
                          ? `Kup ${count} × ${upgrade.label} za ${formatKaucja(cost)}`
                          : `Kup ${upgrade.label} za ${formatKaucja(cost)}`
                      }
                      onClick={() => buy(upgrade.id)}
                    >
                      <span className="bottle-clicker-shop__buy-count" aria-hidden={count <= 1}>
                        {count > 1 ? `×${count}` : ""}
                      </span>
                      <span className="bottle-clicker-shop__buy-price">{formatKaucja(cost)}</span>
                    </Button>
                  ) : (
                    <span className="bottle-clicker-shop__buy bottle-clicker-shop__buy--slot" />
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
        </div>

        <div
          className={`bottle-clicker__panel${mobilePanel === "wyplata" ? " is-open" : ""}`}
          role="dialog"
          aria-label="Wypłata kaucji"
        >
          <div className="bottle-clicker__panel-head">
            <h3>Wypłata kaucji</h3>
            <div className="bottle-clicker__panel-actions">
              <div className="bottle-clicker-coupon-wrap bottle-clicker-coupon-wrap--sheet">
                {renderCouponButton()}
              </div>
              <button type="button" className="bottle-clicker__panel-close" onClick={closeMobilePanel}>
                Zamknij
              </button>
            </div>
          </div>
        <Card variant="panel" icon="🧾" title="Wypłata kaucji">
          <p className="hub-hint bottle-clicker-prestige__copy">
            Po {formatKaucja(BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE)} w tym biegu odbierasz kupon — sklepy i portfel
            wracają do zera, ranking zostaje. Żetony kasyna raz na dobę.
          </p>
          <p className="bottle-clicker-prestige__stats">
            <span>
              Ta wypłata: <strong>{formatMult(nextRunMult)}</strong>
              {` (+${runBonusPct}%)`}
              {save.prestigeCount > 0 ? ` · teraz ${formatMult(runMult)}` : ""}
            </span>
            <span>
              {upcomingTier
                ? `Następna ranga: ${upcomingTier.label} · ${formatChips(upcomingTier.chips)} żet. · ${remainingRestartsCopy(upcomingTier.fromPrestige - save.prestigeCount).toLowerCase()}`
                : `Ranga: ${chipTierFor(save.prestigeCount).label} · ${formatChips(chipReward)} żet.`}
            </span>
          </p>
          <div
            className="bottle-clicker-shop__bar bottle-clicker-prestige__bar"
            role="progressbar"
            aria-label="Postęp do wypłaty"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(prestigeProgress * 100)}
          >
            <span style={{ width: `${Math.round(prestigeProgress * 100)}%` }} />
          </div>
          <p className="bottle-clicker-prestige__status">
            {readyToPrestige
              ? chipsReady
                ? "Możesz wypłacić kaucję."
                : `Możesz wypłacić bez żetonów. Kolejne żetony za ${chipWaitLabel}.`
              : `Jeszcze ${formatKaucja(prestigeLeft)} w tym biegu.`}
          </p>
          <Button
            variant="primary"
            disabled={!readyToPrestige || prestigeBusy}
            onClick={() => void restartRun()}
          >
            {prestigeBusy
              ? "Drukowanie..."
              : readyToPrestige
                ? chipsReady
                  ? `Wypłata · ${formatChips(chipReward)} żet. · kaucja ${formatMult(nextRunMult)}`
                  : `Wypłata · bez żetonów · kaucja ${formatMult(nextRunMult)}`
                : `Wypłata od ${formatKaucja(BOTTLE_CLICKER.PRESTIGE_MIN_GROSZE)}`}
          </Button>
        </Card>
        </div>

        <div
          className={`bottle-clicker__panel${mobilePanel === "ranking" ? " is-open" : ""}`}
          role="dialog"
          aria-label="Ranking kaucji"
        >
          <div className="bottle-clicker__panel-head">
            <h3>Ranking kaucji</h3>
            <button type="button" className="bottle-clicker__panel-close" onClick={closeMobilePanel}>
              Zamknij
            </button>
          </div>
        <Card variant="panel" title="Ranking kaucji" icon="🏆">
          {admin.isAdmin ? (
            <div className="admin-onpage__toolbar">
              <Button size="sm" variant="danger" disabled={admin.busy} onClick={() => void admin.wipe()}>
                Wyczyść tablicę
              </Button>
              <Button size="sm" variant="danger" disabled={admin.busy} onClick={() => void restartAllPlayers()}>
                Wyzeruj wszystkich
              </Button>
            </div>
          ) : null}
          {boardError ? <p className="hub-error">{boardError}</p> : null}
          {!boardReady && !boardError ? (
            <p className="hub-hint">Wczytywanie rankingu...</p>
          ) : board.length === 0 ? (
            boardError ? null : <p className="hub-hint">Jeszcze nikt nie oddał kaucji na koncie.</p>
          ) : (
            <ol className="bottle-clicker-board">
              <li
                className={`bottle-clicker-board__head${admin.isAdmin ? " bottle-clicker-board__row--admin" : ""}`}
                aria-hidden="true"
              >
                <span />
                <span>Gracz</span>
                <span>Łącznie</span>
                <span>But.</span>
                {admin.isAdmin ? <span /> : null}
              </li>
              {board.map((entry, index) => (
                <li
                  key={entry.userId}
                  className={[
                    "bottle-clicker-board__row",
                    user?.id === entry.userId ? "bottle-clicker-board__row--me" : "",
                    admin.isAdmin ? "bottle-clicker-board__row--admin" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="bottle-clicker-board__rank">{index + 1}.</span>
                  <span className="bottle-clicker-board__name">{entry.username}</span>
                  <span className="bottle-clicker-board__kaucja">{formatKaucja(entry.lifetimeGrosze)}</span>
                  <span className="bottle-clicker-board__bottles">{entry.returnedBottles}</span>
                  {admin.isAdmin ? (
                    <AdminDeleteButton
                      confirm={`Usunąć zapis ${entry.username}?`}
                      disabled={admin.busy}
                      onConfirm={() => admin.remove(entry.userId)}
                    />
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Card>
        <BottleClickerOnPageAdmin onChanged={applyCloudSave} />
        </div>
      </aside>
      {couponDrawerOpen
        ? createPortal(
            <WyplataCouponDrawer
              coupons={save.coupons}
              onClose={() => setCouponDrawerOpen(false)}
              onZoom={(coupon) => {
                setCouponDrawerOpen(false);
                setReceipt({ data: couponToReceipt(coupon), printing: false });
              }}
            />,
            document.body,
          )
        : null}
      {receipt
        ? createPortal(
            <WyplataReceiptOverlay
              data={receipt.data}
              printing={receipt.printing}
              onClose={() => setReceipt(null)}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
